import { createContext, useContext } from 'react';

/**
 * Shared React context for the right SidePanel column.
 *
 * Provided by `RightSidePanelLayout.jsx` and consumed by
 * `SidePanelContainer` (in SidePanel.jsx) so the column-level dynamic
 * width and drag-active flag flow into every page rendered inside the
 * right SidePanel without prop-drilling.
 *
 * Lives in its own file to break the circular import that would
 * otherwise exist between SidePanel.jsx and RightSidePanelLayout.jsx.
 *
 * **Pane-aware shape (2026-05-28).** Per `bmaps-sidepanel-independent-
 * columns` study §D3: the context exposes a `panes` map alongside the
 * legacy `width` / `isResizing` fields. The legacy fields resolve to
 * the FIRST open pane's values (or the canonical default if none open),
 * so existing consumers continue working unchanged during the
 * migration. New code SHOULD use `useSidePanelPane(pageId)` for
 * pane-scoped access. The legacy `ctx.width` / `ctx.isResizing`
 * fallbacks are removed in a follow-up cleanup PR once HelpPanel +
 * WorkflowPane confirmed migrated.
 *
 * Default values match "no layout wrapper present" — null width means
 * the SidePanelContainer falls back to its own width prop / default,
 * preserving behavior for left-mounted SidePanels and standalone uses.
 */
export const RightSidePanelContext = createContext({
    // Legacy fields — preserved during migration (D3)
    width: null,
    isResizing: false,
    minimized: false,
    expandFromRail: () => {},
    // Pane-aware shape (D3): { [pageId]: { width, isResizing, minimized, setWidth } }
    panes: {},
    // Helper to resolve a per-pane setter from outside the hook
    setPaneWidth: () => {},
    // Per-pane drag dispatchers (2026-06-01) — wired by
    // RightSidePanelLayout; defaults are no-ops so isolated unit tests
    // mounting a pane component without a provider don't crash.
    beginPaneDrag: () => {},
    updatePaneWidth: () => {},
    endPaneDrag: () => {},
    handleDisabled: false,
});

export function useRightSidePanelLayout() {
    return useContext(RightSidePanelContext);
}

/**
 * Pane-scoped accessor returning `{ width, isResizing, minimized, setWidth }`
 * for the named pane. Use inside components that render INSIDE a
 * `<PaneColumn>` (a stackable pane in the right SidePanel area).
 *
 * Returns sane defaults (width: null, isResizing: false, ...) when the
 * pane is unknown to the context — this happens when a component is
 * rendered outside the right SidePanel (e.g., during a unit test mount
 * without a context provider) and lets the downstream `<SidePanelContainer>`
 * fall back to its own width prop.
 *
 * @param {string} paneId
 * @returns {{ width: number|null, isResizing: boolean, minimized: boolean,
 *             setWidth: function }}
 */
export function useSidePanelPane(paneId) {
    const ctx = useContext(RightSidePanelContext);
    const paneState = (ctx.panes && ctx.panes[paneId]) || null;
    if (paneState) {
        return {
            width: paneState.width,
            isResizing: !!paneState.isResizing,
            minimized: !!paneState.minimized,
            setWidth: (w) => ctx.setPaneWidth(paneId, w),
        };
    }
    // Pane not registered in context — return defaults that let downstream
    // SidePanelContainer fall back to its own prop / default.
    return {
        width: null,
        isResizing: false,
        minimized: false,
        setWidth: () => {},
    };
}
